import { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { setAlert, siteboardAction, } from '../actions/defaultActions';
import { 
  Project, ADD_PROJECT, SET_PATH, UUID, SiteboardApiValidator, ADD_SUBSCRIPTION_ADMIN, getSubscriptionOwnerId, SET_WELCOME_DIALOGUE_OPEN, SET_ALERT, ADD_SCHEDULE,
  SET_EDIT_PROJECT_DIALOGUE_ID, SET_NEW_PROJECT_DIALOGUE_SHOWING,
} from '../actions/types';
import { format } from 'date-fns';
import { keyCountInObject, objectKeysToList, objectValuesToList } from '../Util';

import PopupWindow from './PopupWindow';
import AddProjectMember from './AddProjectMember';
import AddProjectContact from './AddProjectContact';
import CollapsibleContainer from './CollapsibleContainer';
import UserAvatar from './UserAvatar';
import PushButton from './PushButton';
import CalendarPicker from './CalendarPicker';
import ProjectContacts from './ProjectContacts';
import DatePicker from './DatePicker';

// icon imports
import {
  FolderKanbanIcon,
  HashIcon,
  Building2Icon,
  MapPinIcon,
  CalendarDaysIcon,
  Clock3Icon,
  HardHatIcon,
  PhoneIcon,
  UserRoundIcon,
  ChevronDownIcon,
  PlusIcon,
  SlidersHorizontalIcon,
  Check,
  ArrowUpRight,
  ChevronRight,
  Info,
} from 'lucide-react';
import AddIcon from '../icons/addIcon';
import ExpandDownIcon from '../icons/expandDownIcon';

const PAGE_PROJECT_DETAILS = 'PAGE_PROJECT_DETAILS';
const PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS = 'PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS';
const PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST = 'PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST';
const PAGE_PROJECT_CREATED_CREATE_SCHEDULE = 'PAGE_PROJECT_CREATED_CREATE_SCHEDULE';

const inputLabelIconColor = 'text-white/85';

const NewProjectDialogue = ({ 
  _projects,
  _users,
  _userId,
  _subscriptions,
  _subscriptionContacts,
  _projectsContactEmails,
  setAlert,
  siteboardAction,
}) => {

  const [currentPage, setCurrentPage] = useState(PAGE_PROJECT_DETAILS);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [newScheduleStartTimestamp, setNewScheduleStartTimestamp] = useState(null);
  const [subscriptionIdForNewProject, setSubscriptionIdForNewProject] = useState(null);
  const [moreDetailsShowing, setMoreDetailsShowing] = useState(false);

  const projectNameCreateRef = useRef(null);

  const [name, setName] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [address, setAddress] = useState('');
  const [customer, setCustomer] = useState('');
  const [dirNumber, setDirNumber] = useState('');
  const [beginDate, setBeginDate] = useState(format(new Date().getTime(), 'MM/dd/yyyy'));
  const [calendarDays, setCalendarDays] = useState('');
  const [superintendent, setSuperintendent] = useState('');
  const [superintendentPhone, setSuperintendentPhone] = useState('');

  const userCanCreateProjectsForSubscription = objectValuesToList(_subscriptions).filter(sub => SiteboardApiValidator[ADD_PROJECT].isUserIdAuthorized(_userId, sub, null));
  const userMustChooseSubscriptionForNewProject = userCanCreateProjectsForSubscription.length > 1;

  // focus input when component is shown
  useEffect(() => {
    try { projectNameCreateRef.current.focus(); } catch(error) {}
  }, []);

  const getProjectAccountOwnerEmail = () => {
    let projectSubscriptionId = null;
    if(userCanCreateProjectsForSubscription.length > 1 && UUID.isValid(subscriptionIdForNewProject)) {
      projectSubscriptionId = subscriptionIdForNewProject;
    } else if(userCanCreateProjectsForSubscription.length > 0) {
      projectSubscriptionId = userCanCreateProjectsForSubscription[0].id;
    }
    if (projectSubscriptionId === null) return '';
    if(!(projectSubscriptionId in _subscriptions)) return '';
    const subscriptionOwnerId = getSubscriptionOwnerId(_subscriptions[projectSubscriptionId]);
    if(!(subscriptionOwnerId in _users)) return '';
    return _users[subscriptionOwnerId].email; 
  };

  const saveProjectInfo = () => {
    try {
      if(typeof name !== 'string' || name.length === 0) throw new Error('Please enter a project name');

      let projectSubscriptionId = 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa';
      if(userCanCreateProjectsForSubscription.length > 1 && UUID.isValid(subscriptionIdForNewProject)) {
        projectSubscriptionId = subscriptionIdForNewProject;
      } else if(userCanCreateProjectsForSubscription.length > 0) {
        projectSubscriptionId = userCanCreateProjectsForSubscription[0].id;
      }

      const project = Project.validate({
        id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
        ownerId: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
        subscriptionId: projectSubscriptionId,
        members: {},
        schedules: {},
        createdTimestamp: new Date().getTime(),
        modifiedTimestamp: new Date().getTime(), 
        // attributes above here are placeholders to pass validation, they will be overwritten
        name: name,
        jobNumber: jobNumber.trim(),
        address: address,
        customer: customer,
        dirNumber: dirNumber,
        beginDate: beginDate,
        calendarDays: calendarDays.trim().replaceAll(/[\s,]/g, ''),
        superintendent: superintendent,
        superintendentPhone: superintendentPhone,
      });

      siteboardAction(ADD_PROJECT, {project: project}, (output) => { 
        siteboardAction(SET_PATH, { path: 'projectId#'+output.project.id+'#schedule' });
        setCurrentPage(PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS);
        setCreatedProjectId(output.project.id);
        siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: false }); // in case this is used from the welcome screen during onboarding. maybe close welcome another way 
      }); 
    } catch(error) {
      setAlert(error.message); 
    }
  }

  const submitCreateSchedule = () => {
    try {
      if(newScheduleStartTimestamp === null) throw new Error('Choose a schedule beginning date');

      const onSuccess = (output) => {
        closeDialogue();
        siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: false }); // in case this is used from the welcome screen during onboarding. maybe close welcome another way 
      };

      siteboardAction(ADD_SCHEDULE, {
        subscriptionId: createdProject.subscriptionId,
        schedule: {
          id: '00000000-0000-0000-0000-000000000000',
          projectId: createdProject.id,
          beginDate: format(newScheduleStartTimestamp, 'MM/dd/yyyy'),
        },
        isCopyingFromSchedule: false,
        copyFromSchedulId: '00000000-0000-0000-0000-000000000000',
      }, onSuccess);
    } catch(error) {
      siteboardAction(SET_ALERT, { message: error.message }); 
    }
  };

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_NEW_PROJECT_DIALOGUE_SHOWING, { isOpen: false });
  }, [siteboardAction]);

  // Notify that permission is required if a non account manager is trying to create a project
  if(!userCanCreateProjectsForSubscription.length === 0) {
    const ownersAndManagersIdMap = {}; 
    for(const subscriptionId in _subscriptions) {
      for(const userId in _subscriptions[subscriptionId].members) {
        if(SiteboardApiValidator[ADD_SUBSCRIPTION_ADMIN].isUserIdAuthorized(userId, _subscriptions[subscriptionId], null)) {
          ownersAndManagersIdMap[userId] = '';
        }
      }
    }
    return (
      <PopupWindow title={'Permission Required'} closeDialogue={closeDialogue} bgStyle='bg-[#333333]'>
        <div className='flex flex-col gap-y-4 max-w-screen-sm text-white'>
          <strong>You don’t have permission to create a project.</strong>
          <div className='flex flex-col gap-y-2'>
            <div>Only Account Owners and Account Managers can create new projects.</div>
            <div>If you need this access, please contact one of the Account Owners listed below:</div>
          </div>
          <div className='flex flex-col gap-4'>
            {objectKeysToList(ownersAndManagersIdMap).filter(userId => userId in _users).map(userId => _users[userId]).map((user, index) => (
              <div key={index}  className='ml-4'><UserAvatar user={user} showOnlineStatus={false} /></div>
            ))}
          </div>
        </div>
      </PopupWindow>
    );
  }

  const getPopupTitle = () => {
    if(currentPage === PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS) return null;
    if(currentPage === PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST) return null;
    if(currentPage === PAGE_PROJECT_CREATED_CREATE_SCHEDULE) return null;
    return 'Create Project';
  };

  const createdProject = _projects[createdProjectId];
  const newlyCreatedProjectHasAddedUsers = createdProject !== undefined && (keyCountInObject(createdProject.members) + keyCountInObject(createdProject.pendingMembers)) > 1;
  const newlyCreatedProjectUserCount = createdProject ? (keyCountInObject(createdProject.members) + keyCountInObject(createdProject.pendingMembers)) : 0;
  const newlyCreatedProjectHasSchedules = createdProject !== undefined && keyCountInObject(createdProject.schedules) > 0; 
  const newlyCreatedProjectContacts = (() => {
    if(createdProject === undefined) return [];
    if(!(createdProject.subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[createdProject.id] || [])
      .filter((email) => email in _subscriptionContacts[createdProject.subscriptionId])
      .map((email) => _subscriptionContacts[createdProject.subscriptionId][email]);
    return projectContacts;
  })();

  if(userMustChooseSubscriptionForNewProject && subscriptionIdForNewProject === null) {
    return (
      <PopupWindow title={getPopupTitle()} closeDialogue={closeDialogue} bgStyle='bg-[#333333]'>
        <div className='flex flex-col gap-y-4 max-w-[500px] text-white'>
          <div>
            <div>Which account would you like add this project to?</div>
          </div>
          {userCanCreateProjectsForSubscription.map(subscription => (
            <button 
              key={subscription.id}
              className='flex flex-col gap-y-4 rounded-2xl bg-[#222] hover:bg-[#444] text-white shadow-lg p-4 py-6'
              onClick={() => {
                setSubscriptionIdForNewProject(subscription.id);
              }}
            >
              <UserAvatar user={_users[getSubscriptionOwnerId(subscription)]} showOnlineStatus={false} />
            </button>
          ))}
        </div>
      </PopupWindow>
    );
  }

  return (
    <PopupWindow 
      title={getPopupTitle()}
      closeDialogue={currentPage === PAGE_PROJECT_DETAILS ? closeDialogue : null}
      bgStyle='bg-neutral-800'
    >
      {currentPage === PAGE_PROJECT_DETAILS && (
        <div>
          <div className={`flex w-full flex-col gap-6 text-white max-w-screen-sm`}>
            <div className="flex w-full flex-col gap-5 animate-left-fade">
              <div className='w-[90vw] max-w-full' />

              {/* Form content */}
              <div className="flex flex-col gap-5">

                {/* Project Name */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="project-name"
                    className="flex items-center gap-2 text-sm font-semibold text-white/85"
                  >
                    <FolderKanbanIcon size={16} className={inputLabelIconColor} />
                    Project Name
                  </label>

                  <input
                    id="project-name"
                    ref={projectNameCreateRef}
                    type="text"
                    placeholder="e.g. Downtown Office Renovation"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="project-detail-input"
                  />
                </div>

                {/* Optional details toggle - create mode */}
                <div className="overflow-hidden rounded-xl border border-white/20">

                  <button
                    type="button"
                    aria-expanded={moreDetailsShowing}
                    onClick={() => setMoreDetailsShowing(!moreDetailsShowing)}
                    className="
                      group flex w-full items-center justify-between
                      gap-4 px-5 py-4 text-left
                      transition-colors duration-300
                      hover:bg-white/[0.04]
                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-inset
                      focus-visible:ring-[#8060ff]
                    "
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        flex h-9 w-9 items-center justify-center
                        rounded-lg transition-all duration-300
                        ${moreDetailsShowing
                          ? 'bg-[#471cff]/20 text-[#b19fff] shadow-[0_0_20px_rgba(71,28,255,0.15)]'
                          : 'bg-white/5 text-white/50 group-hover:bg-[#471cff]/15 group-hover:text-[#b19fff]'
                        }
                      `}>
                        <div className={`
                          flex h-9 w-9 items-center justify-center
                          rounded-lg transition-colors duration-200
                          ${moreDetailsShowing
                            ? 'bg-neutral-600 text-white/90'
                            : 'bg-white/5 text-white/50 group-hover:bg-neutral-600 group-hover:text-white/90'
                          }
                        `}>
                          <SlidersHorizontalIcon
                            size={18}
                            strokeWidth={1.8}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold text-white/90">
                          Additional Project Details
                        </span>
                        <span className="text-sm text-white/40">
                          Job number, customer, location and more
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <span className="
                        hidden rounded-full border border-white/10
                        bg-white/5 px-2.5 py-1
                        text-[10px] font-medium text-white/45
                        sm:block text-sm
                      ">
                        Optional
                      </span>

                      <ChevronDownIcon
                        size={19}
                        className={`
                          text-white/50
                          transition-transform duration-500
                          ease-[cubic-bezier(0.34,1.56,0.64,1)]
                          ${moreDetailsShowing ? 'rotate-180 text-[#a994ff]' : ''}
                        `}
                      />
                    </div>
                  </button>

                  <CollapsibleContainer isOpen={moreDetailsShowing}>
                    <div className="border-t border-white/10 px-5 pb-5 pt-5">
                      <div className="mb-5 flex items-center gap-1.5 text-sm text-white/40">
                        <Info size={18} className="text-[#f9a928]" />
                        These details can be added or updated later.
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

                        {/* Job Number */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-job-number" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <HashIcon size={16} className="text-white/35" />
                            Job Number
                          </label>
                          <input
                            id="project-job-number"
                            type="text"
                            placeholder="e.g. JOB-2026-001"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={jobNumber}
                            onChange={(event) => setJobNumber(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Customer */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-customer" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <Building2Icon size={16} className="text-white/35" />
                            Customer
                          </label>
                          <input
                            id="project-customer"
                            type="text"
                            placeholder="Customer or company name"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={customer}
                            onChange={(event) => setCustomer(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Address */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                          <label htmlFor="project-address" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <MapPinIcon size={16} className="text-white/35" />
                            Project Address
                          </label>
                          <textarea
                            id="project-address"
                            rows={2}
                            placeholder="Street address, city, state, ZIP"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={address}
                            onChange={(event) => setAddress(event.target.value)}
                            className="project-detail-input resize-y"
                          />
                        </div>

                        {/* Notice to Proceed */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-begin-date" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <CalendarDaysIcon size={16} className="text-white/35" />
                            Notice to Proceed Date
                          </label>
                          <input
                            id="project-begin-date"
                            type="text"
                            placeholder="MM/DD/YYYY"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={beginDate}
                            onChange={(event) => setBeginDate(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Calendar Days */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-calendar-days" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <Clock3Icon size={16} className="text-white/35" />
                            Consecutive Calendar Days
                          </label>
                          <input
                            id="project-calendar-days"
                            type="text"
                            placeholder="e.g. 90"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={calendarDays}
                            onChange={(event) => setCalendarDays(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Superintendent */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-superintendent" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <HardHatIcon size={16} className="text-white/35" />
                            Superintendent
                          </label>
                          <input
                            id="project-superintendent"
                            type="text"
                            placeholder="Full name"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={superintendent}
                            onChange={(event) => setSuperintendent(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Phone */}
                        <div className="flex flex-col gap-2">
                          <label htmlFor="project-superintendent-phone" className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <PhoneIcon size={16} className="text-white/35" />
                            Superintendent Phone
                          </label>
                          <input
                            id="project-superintendent-phone"
                            type="text"
                            placeholder="(555) 123-4567"
                            tabIndex={moreDetailsShowing ? 0 : -1}
                            value={superintendentPhone}
                            onChange={(event) => setSuperintendentPhone(event.target.value)}
                            className="project-detail-input"
                          />
                        </div>

                        {/* Account Owner */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                          <label className="flex items-center gap-2 text-sm font-medium text-white/65">
                            <UserRoundIcon size={16} className="text-white/35" />
                            Project Account Owner
                          </label>
                          <button
                            type="button"
                            tabIndex={moreDetailsShowing && UUID.isValid(subscriptionIdForNewProject) ? 0 : -1}
                            disabled={!UUID.isValid(subscriptionIdForNewProject)}
                            onClick={() => setSubscriptionIdForNewProject(null)}
                            className={`
                              project-detail-input flex items-center justify-between text-left
                              ${UUID.isValid(subscriptionIdForNewProject)
                                ? 'cursor-pointer hover:border-[#8060ff]/50'
                                : 'cursor-default'
                              }
                            `}
                          >
                            <span className="truncate">{getProjectAccountOwnerEmail()}</span>
                            {UUID.isValid(subscriptionIdForNewProject) && (
                              <span className="ml-3 shrink-0 text-sm text-[#a994ff]">
                                Change
                              </span>
                            )}
                          </button>
                        </div>

                      </div>
                    </div>
                  </CollapsibleContainer>
                </div>

              </div>

              {/* Create project button */}
              <div className="flex flex-col gap-3">
                <PushButton
                  textColor="white"
                  bgColor="#471cff"
                  hoverBgColor="#603aff"
                  onClick={saveProjectInfo}
                >
                  <div className="flex items-center justify-center gap-1.5 py-1 font-semibold">
                    <PlusIcon size={19} strokeWidth={2.5} />
                    Create Project
                  </div>
                </PushButton>
              </div>

            </div>

          </div>
        </div>
      )}

      {currentPage === PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS &&
        <div className='flex flex-col gap-4 max-w-screen-md text-white animate-right-fade overflow-hidden'>
          <div className='flex flex-col gap-6 p-4'>
            <ProjectCreatedSuccess />
            <div className='flex flex-col gap-1'>
              <h2 className='text-2xl sm:text-3xl font-bold'>Add your project team</h2>
              <div>Give colleagues access to create and view schedules, collaborate in real time, and stay connected with built-in messaging.</div>
            </div>
            <div className='flex flex-col gap-2'>
              {createdProject && <AddProjectMember project={createdProject } showBorder={false} />}
              {newlyCreatedProjectHasAddedUsers &&
                <PushButton 
                  textColor={'white'}
                  bgColor={'bg-stone-600'}
                  hoverBgColor={'bg-stone-500'}
                  onClick={() => {  
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: createdProjectId, tabName: 'Team' });
                  }}
                >
                  <div className='flex justify-between items-center w-full'>
                    <div className='flex justify-start items-center gap-2'>
                      <Check size='1.4em' className='text-emerald-400' />
                      <b>{newlyCreatedProjectUserCount-1}</b>
                      <div> {(newlyCreatedProjectUserCount-1) === 1 ? 'Project Member' : 'Project Members'} Added</div>
                    </div>
                    <ArrowUpRight size='1.4em' className='text-white' />
                  </div>
                </PushButton>
              }
            </div>
          </div>
          {newlyCreatedProjectHasAddedUsers ?
            <div className='flex justify-between px-2 items-center'>
              <div/>
              <button 
                className='flex justify-end items-center gap-2 opacity-70 hover:opacity-100'
                onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST); }}
              >
                <div className='text-[1.2em] font-medium'>Next</div>
                <ChevronRight />
              </button>
            </div>
            :
            <div className='flex justify-between px-2 items-center'>
              <div/>
              <button 
                className='flex justify-end items-center gap-2 opacity-70 hover:opacity-100'
                onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST); }}
              >
                <div className='text-[1.2em] font-medium'>Skip for now</div>
                <div className='rotate-[-90deg]'><ExpandDownIcon /></div>
              </button>
            </div>
          }
        </div>
      }

      {currentPage === PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST &&
        <div className='flex flex-col gap-4 max-w-screen-md text-white animate-right-fade'>
          <div className='flex flex-col gap-6 p-4'>
            <div className='flex flex-col gap-1'>
              <h2 className='text-2xl sm:text-3xl font-bold'>Create your distribution list</h2>
              <div>Want others to stay in the loop? Add any email address and they’ll get a copy when you publish a schedule for this project.</div>
            </div>
            <div className='flex flex-col gap-2'>
              {createdProjectId && <AddProjectContact projectId={createdProjectId} showBorder={false} />}
              {newlyCreatedProjectContacts.length > 0 &&
                <PushButton 
                  textColor={'white'}
                  bgColor={'bg-stone-600'}
                  hoverBgColor={'bg-stone-500'}
                  onClick={() => {  
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: createdProjectId, tabName: 'Distribution' });
                  }}
                >
                  <div className='flex justify-between items-center w-full'>
                    <div className='flex justify-start items-center gap-2'>
                      <Check size='1.4em' className='text-emerald-400' />
                      <b>{newlyCreatedProjectContacts.length}</b>
                      <div> {newlyCreatedProjectContacts.length === 1 ? 'Email' : 'Emails'} Added</div>
                    </div>
                    <ArrowUpRight size='1.4em' className='text-white' />
                  </div>
                </PushButton>
              }
            </div>
          </div>

          {/* Next button (if contacts have been added), or skip for now */}
          <div className='flex justify-between items-center px-2'>
            <button 
              className='flex items-center gap-2 opacity-70 hover:opacity-100'
              onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_ADD_PROJECT_MEMBERS); }}
            >
              <div className='rotate-[90deg]'><ExpandDownIcon /></div>
              <div className='text-[1.2em] font-medium'>Back</div>
            </button>
            {newlyCreatedProjectContacts.length > 0 
              ?
                <button 
                  className='flex items-center gap-2 opacity-70 hover:opacity-100'
                  onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_CREATE_SCHEDULE); }}
                >
                  <div className='text-[1.2em] font-medium'>Next</div>
                  <ChevronRight />
                </button>
              :
                <button 
                  className='flex items-center gap-2 opacity-70 hover:opacity-100'
                  onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_CREATE_SCHEDULE); }}
                >
                  <div className='text-[1.2em] font-medium'>Skip for now</div>
                  <div className='rotate-[-90deg]'><ExpandDownIcon /></div>
                </button>
            }
          </div>

        </div>
      }

      {currentPage === PAGE_PROJECT_CREATED_CREATE_SCHEDULE &&
        <div className='flex flex-col gap-6 max-w-screen-md text-white animate-right-fade'>
          <div className='flex flex-col gap-6 p-4'>
            <div className='flex flex-col gap-1'>
              <h2 className='text-2xl sm:text-3xl font-bold'>Create your first 3-week schedule</h2>
              <div>{process.env.REACT_APP_WEBSITE_NAME}'s 3-week schedules are a simple and quick way for superintendents and project managers to outline key tasks for the upcoming three weeks, ensuring team alignment and clear communication. It provides a concise overview of current project activities for all stakeholders.</div>
            </div>
            <div className='flex flex-col gap-1.5'>
              <div className='text-xl font-bold tracking-tight text-white'>Choose schedule start date</div>
              <DatePicker
                label="Schedule start date"
                value={newScheduleStartTimestamp}
                onChange={(value) => { setNewScheduleStartTimestamp(value); }}
              />
            </div>
          </div>

          {/*<div className='h-[350px] py-2 rounded-xl'>
            <CalendarPicker selectedDate={beginDate} onDateChange={(dateString) => { setBeginDate(dateString); }} onSubmit={submitCreateSchedule} />
            <div className='text-center text-xl'><span className='font-normal'>Selected Start Date:</span> <b>{format(beginDate, 'iiii, MMMM dd, yyyy')}</b></div>
          </div>*/}

          <div className='flex justify-between items-center px-2 gap-4'>
            <button 
              className='flex items-center gap-2 opacity-70 hover:opacity-100'
              onClick={() => { setCurrentPage(PAGE_PROJECT_CREATED_ADD_DISTRIBUTION_LIST); }}
            >
              <div className='rotate-[90deg]'><ExpandDownIcon /></div>
              <div className='text-[1.2em] font-medium'>Back</div>
            </button>
            <div className=''>
              <PushButton 
                textColor='white'
                bgColor='#471cff'
                hoverBgColor='#603aff'
                onClick={() => { submitCreateSchedule(); }}
              >
                <div className='flex items-center gap-x-1.5'>
                  <AddIcon color='#fff' />
                  <div>Create 3-Week Schedule</div>
                </div>
              </PushButton>
            </div>
          </div>

        </div>
      }
    </PopupWindow>
  );
};

const confettiPieces = Array.from({ length: 10 }, (_, index) => {
  const angle = (index / 10) * Math.PI * 2;

  return {
    x: Math.cos(angle) * 72,
    y: Math.sin(angle) * 64,
    rotation: index * 47,
    color: ['#4ade80', '#f9a928', '#a78bfa'][index % 3],
  };
});

function ProjectCreatedSuccess({
  label = 'Project created',
  className = '',
}) {
  return (
    <div
      role='status'
      className={`flex items-center relative gap-1 ${className}`}
    >
      <style>{`
        .project-created-icon {
          animation: project-created-pop 600ms cubic-bezier(.22, 1, .36, 1) both;
        }

        .project-created-check {
          stroke-dasharray: 1;
          stroke-dashoffset: 0;
          animation: project-created-draw 400ms 250ms ease-out both;
        }

        .project-created-ring {
          animation: project-created-ring 850ms 150ms ease-out both;
        }

        .project-created-confetti {
          opacity: 0;
          animation: project-created-confetti 950ms 200ms ease-out both;
        }

        .project-created-label {
          animation: project-created-label 450ms 300ms ease-out both;
        }

        @keyframes project-created-pop {
          0% { opacity: 0; transform: scale(.65) rotate(-12deg); }
          65% { opacity: 1; transform: scale(1.08) rotate(3deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }

        @keyframes project-created-draw {
          from { stroke-dashoffset: 1; }
          to { stroke-dashoffset: 0; }
        }

        @keyframes project-created-ring {
          0% { opacity: 0; transform: scale(.85); }
          20% { opacity: .6; }
          100% { opacity: 0; transform: scale(1.65); }
        }

        @keyframes project-created-confetti {
          0% {
            opacity: 0;
            transform: translate(0, 0) rotate(0deg) scale(.3);
          }
          15% { opacity: 1; }
          75% { opacity: 1; }
          100% {
            opacity: 0;
            transform:
              translate(var(--confetti-x), var(--confetti-y))
              rotate(var(--confetti-rotation))
              scale(.7);
          }
        }

        @keyframes project-created-label {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .project-created-icon,
          .project-created-check,
          .project-created-label {
            animation: none;
          }

          .project-created-ring,
          .project-created-confetti {
            display: none;
          }
        }
      `}</style>

      <div
        aria-hidden='true'
        className='relative isolate flex shrink-0 items-center justify-center'
        style={{ width: 30, height: 30 }}
      >
        {/* Scale the entire graphic, including confetti */}
        <div
          className='relative flex shrink-0 items-center justify-center'
          style={{ transform: 'scale(1)' }}
        >
          <div className='project-created-ring pointer-events-none absolute h-[1.6em] w-[1.6em] rounded-full border-2 border-green-400/50' />

          {confettiPieces.map((piece, index) => (
            <span
              key={index}
              className='project-created-confetti pointer-events-none absolute left-1/2 top-1/2 -ml-1 -mt-1 h-2 w-2 rounded-[2px]'
              style={{
                backgroundColor: piece.color,
                '--confetti-x': `${piece.x}px`,
                '--confetti-y': `${piece.y}px`,
                '--confetti-rotation': `${piece.rotation}deg`,
              }}
            />
          ))}

          <div className='project-created-icon relative flex h-[1.6em] w-[1.6em] items-center justify-center rounded-full border border-green-400/30 bg-green-400/10 shadow-[0_0_32px_rgba(74,222,128,0.12)]'>
            <svg
              viewBox='0 0 48 48'
              fill='none'
              className='h-[1.6em] w-[1.6em] text-green-400'
            >
              <path
                className='project-created-check'
                pathLength='1'
                d='M12 24.5 20 32 36 16'
                stroke='currentColor'
                strokeWidth='4'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>

      <div className='relative top-[2px] project-created-label text-[1.1em] text-white/80'>
        {label}
      </div>
    </div>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
  _subscriptionContacts: state.serverSynced.subscriptionContacts,
  _projectsContactEmails: state.serverSynced.projectsContactEmails,
}};
export default connect(mapStateToProps, {setAlert, siteboardAction, })(NewProjectDialogue);
